import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { fetchOfferList } from 'actions/OfferActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import type { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import { TRENDING_DESTINATIONS_API_LIMIT } from 'constants/trendingDestinations'
import {
  buildLocalDestinationOfferListFilters,
  LOCAL_DESTINATION_OFFER_ELIGIBILITY_TIMEOUT_MS,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselConstants'
import {
  applyLocalDestinationOfferCarouselEligibilityTimeout,
  selectLocalDestinationOfferCarouselState,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselModel'
import {
  buildLocalDestinationOfferCarouselViewModel,
  getTrendingDestinationsWithIds,
  type LocalDestinationOfferCarouselFilterOption,
  type LocalDestinationOfferCarouselViewModel,
  type TrendingDestinationWithId,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

export type {
  LocalDestinationOfferCarouselFilterOption,
  LocalDestinationOfferCarouselViewModel,
  TrendingDestinationWithId,
}

export type LocalDestinationOfferCarouselState = LocalDestinationOfferCarouselViewModel & {
  trendingDestinationsWithIds: Array<TrendingDestinationWithId>
  checkingEligibility: boolean
  allChecksComplete: boolean
}

export type UseLocalDestinationOfferCarouselOptions = {
  /**
   * When false, skip eligibility fetches and timeout tracking.
   * Used to keep this work off the critical path until the module is near the viewport.
   */
  enabled?: boolean
}

const MAX_ELIGIBILITY_FETCH_RETRIES = 2

export function useLocalDestinationOfferCarousel(
  trendingDestinations: Array<TrendingDestination>,
  options: UseLocalDestinationOfferCarouselOptions = {},
): LocalDestinationOfferCarouselState {
  const { enabled = true } = options
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const [frozenDefaultDestinationId, setFrozenDefaultDestinationId] = useState<string | undefined>(undefined)
  const [eligibilityTimedOut, setEligibilityTimedOut] = useState(false)
  const eligibilityRetryCountsRef = useRef<Record<string, number>>({})
  const [eligibilityExhaustedKeys, setEligibilityExhaustedKeys] = useState<Set<string>>(() => new Set())
  const [eligibilityRetryEpoch, setEligibilityRetryEpoch] = useState(0)

  const destinationsWithIds = useMemo(() => {
    return getTrendingDestinationsWithIds(trendingDestinations)
      .slice(0, TRENDING_DESTINATIONS_API_LIMIT)
  }, [trendingDestinations])

  const destinationIdsKey = destinationsWithIds
    .map(destination => destination.destinationId)
    .join('|')

  const destinationFilters = useMemo(() => {
    return destinationsWithIds.map(destination => buildLocalDestinationOfferListFilters(destination.destinationId))
  }, [destinationsWithIds])

  const listKeys = useMemo(() => {
    return destinationFilters.map(filters => getOfferListKey(filters))
  }, [destinationFilters])

  useEffect(() => {
    setFrozenDefaultDestinationId(undefined)
    setEligibilityTimedOut(false)
    eligibilityRetryCountsRef.current = {}
    setEligibilityExhaustedKeys(new Set())
    setEligibilityRetryEpoch(0)
  }, [destinationIdsKey])

  useEffect(() => {
    if (!enabled) {
      return
    }

    setEligibilityTimedOut(false)
  }, [destinationIdsKey, enabled])

  useEffect(() => {
    if (!enabled || !destinationIdsKey) {
      return
    }

    const timer = setTimeout(() => {
      setEligibilityTimedOut(true)
    }, LOCAL_DESTINATION_OFFER_ELIGIBILITY_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [destinationIdsKey, enabled])

  useEffect(() => {
    if (!enabled) {
      return
    }

    destinationFilters.forEach(filters => {
      dispatch(fetchOfferList(filters))
    })
  }, [canViewLuxPlusBenefits, destinationFilters, dispatch, enabled])

  const offerListStates = useAppSelector((state) => {
    return listKeys.map(key => state.offer.offerLists[key])
  }, shallowEqual)

  useEffect(() => {
    if (!enabled) {
      return
    }

    destinationFilters.forEach((filters, index) => {
      const list = offerListStates[index]
      const listKey = listKeys[index]
      if (!list?.error || list.fetching || !listKey) {
        return
      }

      const retryCount = eligibilityRetryCountsRef.current[listKey] ?? 0
      if (retryCount >= MAX_ELIGIBILITY_FETCH_RETRIES) {
        setEligibilityExhaustedKeys((current) => {
          if (current.has(listKey)) {
            return current
          }

          const next = new Set(current)
          next.add(listKey)
          return next
        })
        return
      }

      eligibilityRetryCountsRef.current[listKey] = retryCount + 1
      dispatch(fetchOfferList(filters))
      setEligibilityRetryEpoch(current => current + 1)
    })
  }, [destinationFilters, dispatch, enabled, eligibilityRetryEpoch, listKeys, offerListStates])

  const eligibilityState = useMemo(() => {
    if (!enabled) {
      return {
        eligibleDestinations: [],
        checkingEligibility: false,
        allChecksComplete: false,
      }
    }

    const state = selectLocalDestinationOfferCarouselState(destinationsWithIds, offerListStates, {
      listKeys,
      exhaustedListKeys: eligibilityExhaustedKeys,
    })
    return applyLocalDestinationOfferCarouselEligibilityTimeout(state, eligibilityTimedOut)
  }, [destinationsWithIds, eligibilityExhaustedKeys, eligibilityTimedOut, enabled, listKeys, offerListStates])

  const { eligibleDestinations, checkingEligibility, allChecksComplete } = eligibilityState

  useEffect(() => {
    if (!enabled) {
      return
    }

    setFrozenDefaultDestinationId((current) => {
      if (current !== undefined && !eligibleDestinations.some(destination => destination.destinationId === current)) {
        return undefined
      }

      if (current === undefined) {
        return eligibleDestinations[0]?.destinationId
      }

      return current
    })
  }, [eligibleDestinations, enabled])

  const viewModel = useMemo(() => {
    return buildLocalDestinationOfferCarouselViewModel(
      intl,
      eligibleDestinations,
      frozenDefaultDestinationId,
    )
  }, [intl, eligibleDestinations, frozenDefaultDestinationId])

  return useMemo(() => ({
    trendingDestinationsWithIds: destinationsWithIds,
    destinations: viewModel.destinations,
    destinationFilterOptions: viewModel.destinationFilterOptions,
    defaultDestinationId: viewModel.defaultDestinationId,
    checkingEligibility,
    allChecksComplete,
  }), [
    allChecksComplete,
    checkingEligibility,
    destinationsWithIds,
    viewModel.defaultDestinationId,
    viewModel.destinationFilterOptions,
    viewModel.destinations,
  ])
}
